import { useMemo, useState } from "react";
import {
  ArrowLeft, Sparkles, Wand2, Scissors, MessageSquare, Copy,
  Save, Check, Loader2, AlertTriangle,
} from "lucide-react";
import Markdown from "@/components/Markdown";
import ExportMenu from "@/components/ExportMenu";
import RichTextEditor from "@/components/RichTextEditor";
import { sectionsForFormat, type DocSection } from "@/content/designDocs";
import { useDesignDocStore } from "@/store/designDocStore";
import { Link2 } from "lucide-react";
import { useDocFormats, useDocSectionMap } from "@/store/designDocContentStore";
import { useAIStore } from "@/store/aiStore";
import { useProgressStore } from "@/store/progressStore";
import { getProvider } from "@/core/ai";
import { AIError } from "@/core/ai/types";

type AIAction = "draft" | "expand" | "tighten" | "critique";

export default function DocEditor({ docId, onBack }: { docId: string; onBack: () => void }) {
  const doc = useDesignDocStore((s) => s.documents.find((d) => d.id === docId));
  const documents = useDesignDocStore((s) => s.documents);
  const rename = useDesignDocStore((s) => s.rename);
  const setValue = useDesignDocStore((s) => s.setValue);
  const setLinks = useDesignDocStore((s) => s.setLinks);
  const formats = useDocFormats();
  const sectionMap = useDocSectionMap();
  const complete = useProgressStore((s) => s.complete);

  const enabled = useAIStore((s) => s.isConfigured());
  const apiKey = useAIStore((s) => s.apiKey);
  const model = useAIStore((s) => s.model);

  const [busy, setBusy] = useState<string | null>(null); // `${sectionId}:${action}`
  const [error, setError] = useState("");
  const [critiques, setCritiques] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const format = useMemo(() => formats.find((f) => f.id === doc?.formatId), [formats, doc?.formatId]);
  const sections = useMemo(
    () => (format ? sectionsForFormat(format, sectionMap).filter((s) => s.id !== "title") : []),
    [format, sectionMap]
  );

  const filled = doc ? sections.filter((s) => (doc.values[s.id] ?? "").trim()).length : 0;

  const toMarkdown = () => {
    if (!doc) return "";
    const parts = [`# ${doc.title}`, ""];
    for (const s of sections) {
      const v = (doc.values[s.id] ?? "").trim();
      parts.push(`## ${s.title}`, v || "_(not written yet)_", "");
    }
    const linked = (doc.links ?? []).map((id) => documents.find((d) => d.id === id)).filter(Boolean);
    if (linked.length) {
      parts.push("## Related", ...linked.map((l) => `- ${l!.title} (${formats.find((f) => f.id === l!.formatId)?.name ?? "doc"})`), "");
    }
    return parts.join("\n");
  };

  if (!doc || !format) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-text">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <p className="text-sm text-muted">This document or its format is no longer available.</p>
      </div>
    );
  }

  async function runAI(section: DocSection, action: AIAction) {
    if (!doc) return;
    setError("");
    setBusy(`${section.id}:${action}`);
    try {
      const provider = getProvider({ apiKey, model });
      const cur = doc.values[section.id] ?? "";
      const others = sections
        .filter((s) => s.id !== section.id && (doc.values[s.id] ?? "").trim())
        .map((s) => `- ${s.title}: ${(doc.values[s.id] ?? "").trim().slice(0, 240)}`)
        .join("\n");
      const system =
        "You are a senior game designer helping author a game design document. Output only the requested section content in Markdown — no preamble and no heading for the section itself. Be concrete and specific; avoid filler.";
      let user: string;
      if (action === "draft") {
        user = `Game: "${doc.title}" (${format!.name} document).\nWrite the "${section.title}" section.\nGuidance: ${section.guidance}${others ? `\n\nOther sections so far:\n${others}` : ""}`;
      } else if (action === "expand") {
        user = `Game: "${doc.title}". Expand and enrich this "${section.title}" section with more concrete detail, keeping the author's intent and voice:\n\n${cur}`;
      } else if (action === "tighten") {
        user = `Tighten this "${section.title}" section for the game "${doc.title}": cut fluff, keep it sharp and concrete. Return only the revised text:\n\n${cur}`;
      } else {
        user = `Give 2–4 short, specific, actionable bullet points of critique on this "${section.title}" section for the game "${doc.title}":\n\n${cur || "(empty)"}`;
      }
      const out = (await provider.ask(system, user, 800)).trim();
      if (action === "critique") {
        setCritiques((c) => ({ ...c, [section.id]: out }));
      } else {
        setValue(doc.id, section.id, out);
      }
    } catch (e) {
      setError(e instanceof AIError ? e.message : "AI request failed. Check your key in Settings.");
    } finally {
      setBusy(null);
    }
  }

  async function copyMarkdown() {
    await navigator.clipboard?.writeText(toMarkdown());
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  async function saveToHistory() {
    if (!doc) return;
    const xp = Math.max(5, Math.round((filled / Math.max(1, sections.length)) * 30));
    await complete(
      {
        refType: "doc",
        refId: doc.formatId,
        categoryIds: [],
        title: `${doc.title} — ${format!.name}`,
        context: `${format!.name} · ${filled}/${sections.length} sections`,
        response: toMarkdown(),
        createdAt: Date.now(),
      },
      xp
    );
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  const links = doc.links ?? [];
  const others = documents.filter((d) => d.id !== doc.id);
  const fmtName = (id: string) => formats.find((f) => f.id === id)?.name ?? "doc";

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-muted hover:text-text">
          <ArrowLeft className="h-4 w-4" /> Documents
        </button>
        <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">{format.name}</span>
        <span className="text-xs text-muted">{filled}/{sections.length} sections written</span>
        <div className="ml-auto flex flex-wrap gap-1.5">
          <button onClick={copyMarkdown} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-muted hover:text-text">
            {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />} Copy
          </button>
          <ExportMenu baseName={doc.title} getMarkdown={toMarkdown} />
          <button onClick={saveToHistory} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-fg shadow-glow transition-transform hover:scale-[1.03]">
            {saved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />} {saved ? "Saved" : "Save to History"}
          </button>
        </div>
      </div>

      {/* Title */}
      <input
        value={doc.title}
        onChange={(e) => rename(doc.id, e.target.value)}
        className="w-full rounded-xl border border-border bg-surface px-4 py-3 font-display text-xl font-semibold text-text focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        placeholder="Game title"
      />

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-sm text-amber-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
        </div>
      )}

      <div className="space-y-4">
          {sections.map((s) => {
            const val = doc.values[s.id] ?? "";
            const hasText = val.trim().length > 0;
            const busyAction = (a: AIAction) => busy === `${s.id}:${a}`;
            return (
              <div key={s.id} className="rounded-2xl border border-border bg-surface p-4">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <h3 className="font-display font-semibold text-text">{s.title}</h3>
                  <span className="text-[11px] text-muted">{val.trim() ? `${val.trim().split(/\s+/).length} words` : "empty"}</span>
                </div>
                <p className="mb-2 text-xs text-muted">{s.guidance}</p>
                <RichTextEditor
                  value={val}
                  onChange={(md) => setValue(doc.id, s.id, md)}
                  placeholder={`${s.guidance} — Markdown shortcuts work (# heading, - list, > quote).`}
                  minHeight="4.5rem"
                />
                {enabled && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-primary">AI</span>
                    <AIButton label="Draft" icon={Sparkles} loading={busyAction("draft")} disabled={!!busy} onClick={() => runAI(s, "draft")} />
                    <AIButton label="Expand" icon={Wand2} loading={busyAction("expand")} disabled={!!busy || !hasText} onClick={() => runAI(s, "expand")} />
                    <AIButton label="Tighten" icon={Scissors} loading={busyAction("tighten")} disabled={!!busy || !hasText} onClick={() => runAI(s, "tighten")} />
                    <AIButton label="Critique" icon={MessageSquare} loading={busyAction("critique")} disabled={!!busy || !hasText} onClick={() => runAI(s, "critique")} />
                  </div>
                )}
                {critiques[s.id] && (
                  <div className="mt-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-text/90">
                    <div className="mb-1 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-primary">
                      <span>Critique</span>
                      <button onClick={() => setCritiques((c) => { const n = { ...c }; delete n[s.id]; return n; })} className="text-muted hover:text-text">dismiss</button>
                    </div>
                    <Markdown source={critiques[s.id]} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

      {others.length > 0 ? (
          <div className="rounded-2xl border border-border bg-surface p-4">
            <div className="mb-1 flex items-center gap-1.5">
              <Link2 className="h-4 w-4 text-primary" />
              <h3 className="font-display font-semibold text-text">Related systems, mechanics & features</h3>
            </div>
            <p className="mb-2 text-xs text-muted">Link this to other documents so your systems, features, and mechanics cross-reference. Links appear in the exported doc.</p>
            <div className="flex flex-wrap gap-1.5">
              {others.map((o) => {
                const on = links.includes(o.id);
                return (
                  <button
                    key={o.id}
                    onClick={() => setLinks(doc.id, on ? links.filter((x) => x !== o.id) : [...links, o.id])}
                    className={["rounded-lg border px-2.5 py-1 text-xs transition-colors", on ? "border-primary/60 bg-primary/10 text-text" : "border-border bg-surface-2 text-muted hover:text-text"].join(" ")}
                  >
                    {o.title} <span className="opacity-60">· {fmtName(o.formatId)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted">Create another document to cross-link systems, features, and mechanics here.</p>
        )}

      {!enabled && (
        <p className="text-xs text-muted">
          Tip: turn on optional AI in <span className="text-text">Settings</span> (with your own Anthropic key) to draft, expand, tighten, and critique sections. Everything here works fully without it.
        </p>
      )}
    </div>
  );
}

function AIButton({
  label, icon: Icon, loading, disabled, onClick,
}: {
  label: string;
  icon: typeof Sparkles;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1 rounded-md border border-border bg-surface-2 px-2 py-1 text-xs text-muted transition-colors hover:text-text disabled:opacity-40"
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />} {label}
    </button>
  );
}
